import { useCallback, useMemo, type FC, type ReactNode } from "react";

import { nodeTitle } from "@/features/caseFiles/nodeNames";
import type { FsNode } from "../../caseFiles/types";
import {
  CitationSourceProvider,
  type CitationSource
} from "../../draftCitations/CitationSource";
import { useCaseIde } from "../context/CaseIdeContext";
import { useCaseData } from "../data/CaseDataContext";

/**
 * Lets citation chips anywhere in the IDE (chat redlines, the review drawer)
 * resolve against the case's live labels and open files in the center pane.
 */
export const CaseCitationSource: FC<{ children: ReactNode }> = ({
  children
}) => {
  const { individualId, open } = useCaseIde();
  const { fs } = useCaseData();

  const openFile = useCallback(
    (node: FsNode, page: number | null) => {
      open({ kind: "file", id: node.id, title: nodeTitle(node), page });
    },
    [open]
  );

  const value = useMemo<CitationSource>(
    () => ({
      individualId,
      nodes: fs.nodes,
      labels: fs.labels,
      scheme: fs.scheme,
      open: openFile
    }),
    [individualId, fs.nodes, fs.labels, fs.scheme, openFile]
  );

  return (
    <CitationSourceProvider value={value}>{children}</CitationSourceProvider>
  );
};
